<template>
    <!--
        The same lozenge-in-a-box status changer the sales actions index and the
        order services list use (`sales/SalesActionStatusSelect.vue`,
        `order/services/status/ServiceStatusChanger.vue`), so a status reads and
        behaves identically wherever it appears.

        This holds only the presentation and the choice; the caller performs the
        write. Every status is offered in both directions - the API imposes no
        transition order.

        `type="button"` is load-bearing: the trigger can sit inside a `<form>`,
        where a bare button submits.
    -->
    <MPDropdown.Root>
        <MPDropdown.Trigger>
            <button
                type="button"
                class="cursor-pointer outline-hidden"
                :disabled="disabled"
                @click.stop
            >
                <div
                    class="border-muval-gray-5 rounded-[8px] border p-1"
                    :class="disabled ? '' : 'hover:border-muval-gray-4'"
                >
                    <Lozenge :class="[statusClasses, { 'cursor-not-allowed opacity-50': disabled }]">
                        {{ statusLabel }}
                    </Lozenge>
                </div>
            </button>
        </MPDropdown.Trigger>

        <MPDropdown.Content align="start">
            <MPDropdown.Item
                v-for="option in COMPANY_DIARY_STATUS_OPTIONS"
                :key="option.value"
                @select="choose(option.value)"
            >
                <Lozenge
                    :class="companyDiaryStatusClasses(option.value)"
                    class="mr-auto text-xs"
                >
                    {{ option.label }}
                </Lozenge>
            </MPDropdown.Item>
        </MPDropdown.Content>
    </MPDropdown.Root>
</template>

<script setup lang="ts">
import Lozenge from '@components/Lozenge.vue';
import { MPDropdown } from '@components/dropdown';
import {
    COMPANY_DIARY_STATUS_OPTIONS,
    companyDiaryStatusClasses,
    companyDiaryStatusLabel,
    type CompanyDiaryStatus,
} from './companyDiaryStatusOptions';

const props = withDefaults(
    defineProps<{
        /** The status shown on the trigger. */
        modelValue?: string | null;
        disabled?: boolean;
    }>(),
    {
        modelValue: 'TO_DO',
        disabled: false,
    },
);

const emit = defineEmits<{
    'update:modelValue': [value: CompanyDiaryStatus];
}>();

/**
 * An empty value reads as TO_DO rather than as a blank lozenge. The prop default
 * does not cover it: a caller binding `entry?.status` passes an explicit null,
 * which `withDefaults` leaves alone.
 */
const status = computed(() => props.modelValue || 'TO_DO');

const statusLabel = computed(() => companyDiaryStatusLabel(status.value));
const statusClasses = computed(() => companyDiaryStatusClasses(status.value));

function choose(value: CompanyDiaryStatus) {
    if (value === status.value) {
        return;
    }

    emit('update:modelValue', value);
}
</script>
